import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircleHelp, Lightbulb, Mic, MicOff, X } from "lucide-react";
import { PaywallModal } from "@/components/paywall";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { CATALOG_SITUATIONS } from "@/data/catalogScenarios";
import { findCatalogSituation } from "@/lib/catalog";
import { Analytics } from "@/lib/analytics";
import { isRepairTurn, sceneBrief, sceneHint } from "@/lib/sceneCast";
import { usePaywall } from "@/hooks/usePaywall";
import { useUsageTracker } from "@/hooks/useUsageTracker";
import { useVoiceAgent } from "@/hooks/useVoiceAgent";
import { Button } from "@/components/ui/button";

const GLASS = "border border-white/15 bg-white/10 text-white backdrop-blur-md";

const PracticeTalk = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const situation = findCatalogSituation(CATALOG_SITUATIONS, slug);
  const openedResult = useRef(false);
  const openResult = useCallback((sessionId: string | null) => {
    if (openedResult.current) return;
    openedResult.current = true;
    if (sessionId) {
      navigate(`/app/review/${sessionId}`, { replace: true });
      return;
    }
    navigate("/app");
  }, [navigate]);

  const { status, transcripts, connect, endSession } = useVoiceAgent({
    catalogSituation: situation,
    onSessionPersisted: (result) => {
      if (result.sessionId) openResult(result.sessionId);
    },
  });
  const { isPaid } = useSubscription();
  const { canUse, monthlyMinutes } = useUsageTracker();
  const { modalState, showPaywall, hidePaywall } = usePaywall();
  const [hintLevel, setHintLevel] = useState(0);
  const [showHint, setShowHint] = useState(false);

  const brief = situation ? sceneBrief(situation) : null;
  const cleanLines = transcripts
    .map((item) => ({ role: item.role, text: item.text.replace(/\[final\]/g, "").trim() }))
    .filter((item) => item.text);
  const lastPartnerLine = [...cleanLines].reverse().find((item) => item.role === "assistant")?.text;
  const lastUserLine = [...cleanLines].reverse().find((item) => item.role === "user")?.text;
  const heardRepair = Boolean(lastPartnerLine && isRepairTurn(lastPartnerLine));

  const handleConnect = () => {
    if (!isPaid || !canUse("voice")) {
      showPaywall("voice", monthlyMinutes.used, monthlyMinutes.limit);
      return;
    }
    connect();
    Analytics.voiceStarted(situation?.slug);
  };

  const handleEnd = async () => {
    try {
      const sessionId = await endSession();
      openResult(sessionId);
    } catch (error) {
      console.error(error);
      openResult(null);
    }
  };

  const handleHint = () => {
    if (!showHint) {
      setShowHint(true);
      setHintLevel(1);
      Analytics.voiceHint(1, situation?.slug);
      return;
    }
    const next = Math.min(3, hintLevel + 1);
    setHintLevel(next);
    Analytics.voiceHint(next, situation?.slug);
  };

  const hintText = useMemo(() => {
    if (!situation) return "";
    return sceneHint(situation, hintLevel);
  }, [hintLevel, situation]);

  if (!situation || !brief) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-50 px-6">
        <p className="text-neutral-900">Essa situação não existe.</p>
        <Button className="mt-4" onClick={() => navigate("/app")}>
          Voltar ao início
        </Button>
      </div>
    );
  }

  const talking = status === "listening" || status === "speaking";
  const helperLine = status === "connecting"
    ? "Conectando…"
    : status === "speaking"
      ? `${brief.partner.name} está falando`
      : "Fale agora · ajuda em português se precisar";
  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-blue-900 px-5 pb-8 pt-4 text-white md:px-8">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[18%] left-1/2 h-[72%] w-[150%] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgb(22_83_230_/_0.4)_0%,transparent_72%)]" />
        <div className="absolute -bottom-[12%] -right-[18%] h-[58%] w-[80%] bg-[radial-gradient(ellipse_at_center,rgb(255_122_26_/_0.16)_0%,transparent_72%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgb(4_10_24_/_0.35)_0%,rgb(4_10_24_/_0.55)_30%,rgb(4_10_24_/_0.9)_100%)]" />
      </div>

      <header className="relative mx-auto flex w-full max-w-lg items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => void handleEnd()}
          aria-label="Encerrar ensaio"
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${GLASS}`}
        >
          <X className="h-5 w-5" />
        </button>
        <p className={`min-w-0 flex-1 truncate rounded-full px-4 py-2 text-center text-[13px] font-medium ${GLASS}`}>
          {brief.goal.charAt(0).toUpperCase() + brief.goal.slice(1)}
        </p>
        <button
          type="button"
          onClick={handleHint}
          aria-label="Pedir uma dica"
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${GLASS}`}
        >
          <CircleHelp className="h-5 w-5" />
        </button>
      </header>

      <main className="relative mx-auto flex w-full max-w-lg flex-1 flex-col items-center justify-center py-6">
        <div
          className={`overflow-hidden rounded-full border border-white/20 bg-white p-1 ${
            status === "speaking" ? "ring-2 ring-blue-500" : ""
          }`}
        >
          <img
            src={brief.partner.src}
            alt={brief.partner.label}
            className="h-52 w-52 rounded-full object-cover object-[center_18%] md:h-60 md:w-60"
          />
        </div>
        <p className="mt-3 text-[13px] text-white/70">{brief.partner.label}</p>
      </main>

      <footer className="relative mx-auto flex w-full max-w-lg flex-col gap-4">
        <div className={`rounded-[26px] px-5 py-5 ${GLASS}`}>
          {lastUserLine && (
            <p className="mb-2 text-[14px] leading-snug text-white/60">Você: {lastUserLine}</p>
          )}
          <p className="text-[19px] font-bold leading-snug">
            &ldquo;{lastPartnerLine ?? brief.partnerOpen}&rdquo;
          </p>
          <p className="mt-2.5 text-[13px] text-blue-100">
            {heardRepair
              ? "A pessoa não captou. Pode repetir com calma — o microfone continua aberto."
              : helperLine}
          </p>
          {showHint && (
            <p className="mt-2.5 border-t border-white/10 pt-2.5 text-[14px] leading-snug text-white/85">
              <span className="font-semibold text-orange-300">Talki · </span>
              {hintText}
            </p>
          )}
        </div>

        <div className="flex items-center justify-center gap-6 pb-1">
          <button
            type="button"
            onClick={() => void handleEnd()}
            aria-label="Encerrar ensaio"
            className={`flex h-12 w-12 items-center justify-center rounded-full ${GLASS}`}
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={handleConnect}
            disabled={status === "connecting" || talking}
            aria-label={talking ? helperLine : "Começar a falar"}
            className={`flex h-20 w-20 items-center justify-center rounded-full bg-blue-500 text-white shadow-[0_14px_36px_rgba(43,107,255,0.5)] transition disabled:opacity-80 ${
              talking ? "animate-pulse" : "hover:scale-105"
            }`}
          >
            {talking ? <MicOff className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
          </button>
          <button
            type="button"
            onClick={handleHint}
            aria-label={showHint && hintLevel < 3 ? "Mais uma dica" : "Pedir uma dica"}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-600 text-white shadow-[0_8px_22px_rgba(255,122,26,0.4)] transition hover:scale-105"
          >
            <Lightbulb className="h-5 w-5" />
          </button>
        </div>
      </footer>

      <PaywallModal
        isOpen={modalState.isOpen}
        onClose={hidePaywall}
        type={modalState.type}
        used={modalState.used}
        limit={modalState.limit}
      />
    </div>
  );
};

export default PracticeTalk;
